// Block E (Phase 3): facade поверх @telegram-apps/sdk-react v3.
//
// Зачем фасад, а не «голый» import везде:
// - Сохраняем тонкий API (`getWebApp`, `haptic`, `cloudStorage`, `biometric` и т.д.),
//   который уже используют 12 файлов client-webapp. Заменили бы импорты — пришлось бы
//   синхронно переписывать вызовы (MainButton/BackButton в SDK v3 — асинхронно mounted).
// - SDK всё равно поднят в `main.tsx` через `init()`, поэтому helpers под капотом
//   используют типобезопасные обёртки SDK (`hapticFeedback`, `viewport.requestFullscreen`,
//   `cloudStorage`, `biometry`, `miniApp.setHeaderColor/setBackgroundColor`).
// - Для прямых вызовов TG (MainButton.onClick, BackButton.show) callsite-ы продолжают
//   использовать `getWebApp()` → `window.Telegram.WebApp` — SDK с тем же объектом работает,
//   конфликта нет.

import {
  hapticFeedback,
  viewport,
  miniApp,
  cloudStorage as sdkCloudStorage,
  biometry,
} from '@telegram-apps/sdk-react';
import { deriveBrandPalette, readableOn, HEX6 } from './color';
import i18n from '@/i18n';

// Текущий основной brand-цвет (дефолт = голубой). Обновляется applyBrandPrimary,
// используется для MainButton/CTA-кнопки Telegram.
let brandPrimary = '#3390EC';

/** Фон приложения, тот же, что `--color-background` в index.css. */
const APP_BACKGROUND = '#0a0a0c';

type TgUser = {
  id: number;
  is_bot?: boolean;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
};

type TgThemeParams = Partial<{
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
  header_bg_color: string;
  accent_text_color: string;
  section_bg_color: string;
  section_header_text_color: string;
  subtitle_text_color: string;
  destructive_text_color: string;
}>;

type TgWebApp = {
  ready: () => void;
  expand: () => void;
  close: () => void;
  platform: string;
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  initData: string;
  initDataUnsafe: { user?: TgUser; start_param?: string };

  isFullscreen?: boolean;
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  hideKeyboard?: () => void;

  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;

  MainButton: {
    /** Текущая подпись кнопки (SDK держит её у себя). */
    text?: string;
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  showPopup: (
    params: {
      title?: string;
      message: string;
      buttons?: Array<{ id?: string; type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'; text?: string }>;
    },
    callback?: (button_id: string | null) => void,
  ) => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const getWebApp = (): TgWebApp | null => {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    return window.Telegram.WebApp;
  }
  return null;
};

export const getThemeParams = (): TgThemeParams => {
  return getWebApp()?.themeParams || {};
};

export const getTgUser = (): TgUser | null => {
  return getWebApp()?.initDataUnsafe?.user ?? null;
};

export const initTelegram = (): void => {
  const webApp = getWebApp();
  if (webApp) {
    webApp.ready();
    webApp.expand();
    // Фон вебвью и полоса под MainButton в цвет приложения (design-8). По умолчанию
    // Telegram красит их цветом своей темы, и в светлой теме вокруг тёмного
    // интерфейса торчал белый: при оттягивании страницы и под кнопкой оплаты.
    // Шапку не трогаем, её ведёт основной цвет бота (applyBrandPrimary).
    try {
      if (webApp.isVersionAtLeast?.('6.1')) webApp.setBackgroundColor(APP_BACKGROUND);
      if (webApp.isVersionAtLeast?.('7.10')) webApp.setBottomBarColor?.(APP_BACKGROUND);
    } catch {
      // старые клиенты — останется цвет темы
    }
    // MainButton по умолчанию в светлой теме клиента отдаётся белым — насильно
    // выставляем brand-цвет, чтобы кнопка «Оплатить» не выглядела чужеродно.
    try {
      webApp.MainButton.setParams({ color: brandPrimary, text_color: readableOn(brandPrimary) });
    } catch {
      // older clients без setParams — игнор
    }
  }
  // Блокируем pinch-zoom и double-tap zoom в iOS Safari — там meta-viewport
  // user-scalable=no игнорируется в новых версиях. На Android достаточно meta.
  if (typeof document !== 'undefined') {
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('gesturechange', (e) => e.preventDefault());
    document.addEventListener('gestureend', (e) => e.preventDefault());
    // double-tap zoom — отлавливаем 2 быстрых тапа в одном месте
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    }, { passive: false });
  }
};

export const haptic = (kind: 'light' | 'success' | 'warning' | 'error' = 'light'): void => {
  try {
    if (kind === 'light') {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred('light');
        return;
      }
    } else {
      if (hapticFeedback.notificationOccurred.isAvailable()) {
        hapticFeedback.notificationOccurred(kind);
        return;
      }
    }
  } catch {
    // SDK не инициализирован / окружение неизвестно — fallback на raw WebApp.
  }
  const tg = getWebApp();
  if (!tg?.HapticFeedback) return;
  if (kind === 'light') tg.HapticFeedback.impactOccurred('light');
  else tg.HapticFeedback.notificationOccurred(kind);
};

/**
 * Block B.8: подписка на смену темы Telegram (юзер переключает dark↔light в TG).
 * Возвращает unsubscribe-функцию. Подписка через raw onEvent — sdk-react даёт сигналы,
 * но callsite-ы привыкли к callback-API.
 */
export const onThemeChanged = (cb: (params: TgThemeParams) => void): (() => void) => {
  const tg = getWebApp();
  if (!tg) return () => {};
  const handler = () => cb(tg.themeParams);
  tg.onEvent('themeChanged', handler);
  return () => tg.offEvent('themeChanged', handler);
};

// ---------- Block E.3: fullscreen ----------

/** Платформы где fullscreen имеет смысл: мобильные клиенты Telegram. На десктопе
 *  WebApp в отдельном окне — fullscreen разворачивает его на весь экран ПК, и юзер
 *  теряет окно Telegram, доступ к другим чатам и так далее. На iPad спорно, но
 *  тоже больше мешает. Включаем только iOS/Android. */
const FULLSCREEN_PLATFORMS = new Set(['ios', 'android', 'android_x']);

export const requestFullscreen = (): boolean => {
  const tg = getWebApp();
  const platform = tg?.platform ?? 'unknown';
  if (!FULLSCREEN_PLATFORMS.has(platform)) return false;
  try {
    if (viewport.requestFullscreen.isAvailable()) {
      void viewport.requestFullscreen();
      return true;
    }
  } catch {
    // ignore
  }
  if (!tg || typeof tg.requestFullscreen !== 'function') return false;
  try {
    tg.requestFullscreen();
    return true;
  } catch {
    return false;
  }
};

// ---------- Block E.4: hideKeyboard ----------

export const hideKeyboard = (): void => {
  const tg = getWebApp();
  if (!tg || typeof tg.hideKeyboard !== 'function') return;
  try {
    tg.hideKeyboard();
  } catch {
    // ignore
  }
};

// ---------- Основной цвет Mini App: палитра из одного hex ----------

/**
 * Применить основной brand-цвет: пересчитать акцентную палитру и переопределить
 * CSS-переменные (--color-primary/--color-accent/--brand-*). Все голубые акценты
 * интерфейса завязаны на эти переменные, поэтому перекрашиваются мгновенно.
 *
 * Нативную шапку Telegram тоже ведём от основного цвета (Q3); фон оставляем тёмным
 * — его при необходимости переопределит setBrandColors(header_color, bg_color).
 * Вызывать ДО setBrandColors, чтобы явные header/bg партнёра имели приоритет.
 */
const BRAND_VARS = [
  '--color-primary',
  '--color-accent',
  '--brand-primary-rgb',
  '--brand-accent-rgb',
  '--brand-cta-from',
  '--brand-cta-to',
  '--brand-cta-foreground',
  '--color-primary-foreground',
] as const;

const DEFAULT_PRIMARY = '#3390EC';

/**
 * Сбросить тему к дефолтной. Раньше `applyBrandPrimary(null)` просто выходил из
 * функции, и цвет предыдущего бота оставался висеть: мини-апп у всех ботов живёт
 * на одном адресе, а значит и в одном localStorage. Партнёр красил один бот в
 * розовый и видел розовым второй, хотя в настройках второго было пусто.
 *
 * Снимаем свойства, а не пересчитываем палитру от дефолтного цвета: тогда
 * подхватываются ровно те значения, что написаны в `index.css`, без дрейфа на
 * округлениях.
 */
const resetBrandPrimary = (): void => {
  brandPrimary = DEFAULT_PRIMARY;
  const root = document.documentElement.style;
  for (const name of BRAND_VARS) root.removeProperty(name);
  try {
    getWebApp()?.setHeaderColor?.('bg_color');
  } catch {
    // старые клиенты Telegram строковое значение не принимают — шапка останется прежней
  }
  try {
    getWebApp()?.MainButton?.setParams?.({ color: DEFAULT_PRIMARY, text_color: '#ffffff' });
  } catch {
    // ignore
  }
};

export const applyBrandPrimary = (primary?: string | null): void => {
  if (!primary || !HEX6.test(primary)) {
    resetBrandPrimary();
    return;
  }
  brandPrimary = primary;
  const p = deriveBrandPalette(primary);
  const root = document.documentElement.style;
  root.setProperty('--color-primary', p.primary);
  root.setProperty('--color-accent', p.accent);
  root.setProperty('--brand-primary-rgb', p.primaryRgb);
  root.setProperty('--brand-accent-rgb', p.accentRgb);
  root.setProperty('--brand-cta-from', p.ctaFrom);
  root.setProperty('--brand-cta-to', p.ctaTo);

  // Надпись на кнопке — не всегда белая: на светлом бренде (жёлтый, лайм,
  // мятный) белым по кнопке читалось 1.1–1.6:1, то есть не читалось вовсе.
  root.setProperty('--brand-cta-foreground', p.ctaForeground);
  root.setProperty('--color-primary-foreground', p.primaryForeground);

  // Нативная шапка Telegram = основной цвет.
  try {
    if (miniApp.setHeaderColor.isAvailable()) {
      miniApp.setHeaderColor(primary as `#${string}`);
    } else {
      getWebApp()?.setHeaderColor?.(primary);
    }
  } catch {
    // ignore
  }
  // MainButton, если уже смонтирован.
  try {
    getWebApp()?.MainButton?.setParams?.({ color: primary, text_color: p.primaryForeground });
  } catch {
    // ignore
  }
};

// ---------- Block E.5: brand colors (header / background) ----------

export const setBrandColors = (header?: string | null, bg?: string | null): void => {
  const hex6 = /^#[0-9a-fA-F]{6}$/;
  try {
    if (header && hex6.test(header) && miniApp.setHeaderColor.isAvailable()) {
      miniApp.setHeaderColor(header as `#${string}`);
    }
    if (bg && hex6.test(bg) && miniApp.setBackgroundColor.isAvailable()) {
      miniApp.setBackgroundColor(bg as `#${string}`);
    }
    return;
  } catch {
    // fall through to raw
  }
  const tg = getWebApp();
  if (!tg) return;
  try {
    if (header && hex6.test(header)) tg.setHeaderColor(header);
    if (bg && hex6.test(bg)) tg.setBackgroundColor(bg);
  } catch {
    // ignore
  }
};

// ---------- Подпись MainButton без отставания (#210) ----------

/** Невидимый символ (WORD JOINER): второе событие отличается от первого только им. */
const MAIN_TEXT_NUDGE = '\u2060';
let mainTextSeq = 0;

/**
 * Поставить подпись MainButton так, чтобы клиент Telegram на Android её показал.
 *
 * Партнёр прислал скрины (#210): выбираешь «CryptoBot», а на кнопке «ОПЛАТИТЬ ЧЕРЕЗ
 * ЗВЁЗДЫ», то есть предыдущий выбор. Мини-апп шлёт верный текст одним событием, а
 * клиент на Android применяет его с отставанием на одно событие. Поэтому подпись уходит
 * дважды: сразу и через 120 мс тем же текстом с невидимым символом (SDK отсекает
 * повтор одинакового состояния, нужен хоть один отличающийся байт). Отстающий клиент
 * покажет первое из двух, и это уже нужный текст. Новый вызов отменяет досылку старого.
 */
export function setMainButtonText(text: string, params: { is_visible?: boolean } = {}): void {
  const tg = getWebApp();
  if (!tg?.MainButton) return;
  tg.MainButton.setParams({ text, ...params });
  const seq = ++mainTextSeq;
  window.setTimeout(() => {
    if (seq !== mainTextSeq) return;
    const button = getWebApp()?.MainButton;
    // Экран успел сменить подпись мимо этого помощника (setText/setParams на другом
    // экране) — досылка старой затёрла бы новую. Шлём, только если подпись наша.
    if (!button || (button.text !== undefined && button.text !== text)) return;
    button.setParams({ text: text + MAIN_TEXT_NUDGE });
  }, 120);
}

// ---------- Block E.6: CloudStorage wrapper (Promise-based) ----------

/**
 * Универсальный открыватель invoice-URL'ов от платёжных провайдеров.
 * - `https://t.me/...` (CryptoBot pay_url / mini_app_invoice_url, гифт-ссылки) → `openTelegramLink`
 *   (остаёмся в Telegram, payment-flow внутри).
 * - Всё остальное → `openLink` (внешний браузер: Lava / Heleket / LZT).
 *
 * Без этого CryptoBot-инвойсы (t.me/CryptoBot/...) открывались в **браузере** через openLink —
 * юзер должен был перелогиниться в Telegram-web, плохой UX, и mini_app деплинк не работал.
 */
export const openInvoiceUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const tg = getWebApp();
  if (!tg) {
    if (typeof window !== 'undefined') window.open(url, '_blank');
    return true;
  }
  if (url.startsWith('https://t.me/') || url.startsWith('http://t.me/') || url.startsWith('tg://')) {
    try {
      tg.openTelegramLink(url);
      return true;
    } catch {
      // fall through to openLink
    }
  }
  try {
    tg.openLink(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Открыть внешнюю ссылку (канал, сайт). Развилка та же, что у платёжных ссылок:
 * `t.me` остаётся внутри Telegram, остальное уходит в браузер. Имя отдельное, потому
 * что на месте вызова «openInvoiceUrl» для ссылки на канал читается неверно.
 */
export const openExternalUrl = openInvoiceUrl;

export const cloudStorage = {
  isAvailable: (): boolean => {
    try {
      return sdkCloudStorage.setItem.isAvailable();
    } catch {
      return Boolean(getWebApp()?.['CloudStorage' as keyof TgWebApp]);
    }
  },

  setItem: async (key: string, value: string): Promise<boolean> => {
    try {
      if (sdkCloudStorage.setItem.isAvailable()) {
        await sdkCloudStorage.setItem(key, value);
        return true;
      }
    } catch {
      // ignore — return false
    }
    return false;
  },

  getItem: async (key: string): Promise<string | null> => {
    try {
      if (sdkCloudStorage.getItem.isAvailable()) {
        const result = await sdkCloudStorage.getItem(key);
        return typeof result === 'string' ? result || null : null;
      }
    } catch {
      // ignore
    }
    return null;
  },

  removeItem: async (key: string): Promise<boolean> => {
    try {
      if (sdkCloudStorage.deleteItem.isAvailable()) {
        await sdkCloudStorage.deleteItem(key);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  },
};

// ---------- Block E.7: BiometricManager (Promise-based) ----------

export const biometric = {
  isAvailable: (): boolean => {
    try {
      return biometry.isAvailable();
    } catch {
      return false;
    }
  },

  init: async (): Promise<void> => {
    try {
      if (biometry.mount.isAvailable() && !biometry.isMounted()) {
        await biometry.mount(undefined);
      }
    } catch {
      // ignore — fail-closed в authenticate()
    }
  },

  /**
   * Возвращает 'ok' если биометрия сработала, 'unavailable' если не поддерживается
   * (callsite сам решает что делать — обычно показать TG-popup confirm), 'denied' если
   * юзер отказал. Никогда не возвращает 'ok' при ошибке/отсутствии — fail-closed.
   *
   * ВАЖНО: `biometry.isAvailable()` в SDK возвращает true только ПОСЛЕ успешного mount.
   * Поэтому всегда монтируем сначала, потом проверяем доступность — иначе
   * на любом устройстве/клиенте получим false ещё до того как SDK подтвердит наличие
   * BiometricManager у Telegram-клиента.
   */
  authenticate: async (reason = i18n.t('Confirm action')): Promise<'ok' | 'unavailable' | 'denied'> => {
    try {
      // 1. Mount — заставляем SDK запросить BiometricManager.init() у TG
      if (biometry.mount.isAvailable()) {
        try {
          if (!biometry.isMounted()) {
            await biometry.mount(undefined);
          }
        } catch {
          return 'unavailable';
        }
      }
      // 2. Только теперь проверяем доступность реального BiometricManager
      if (!biometry.isAvailable()) return 'unavailable';
      // 3. Запрашиваем разрешение (юзер видит «бот хочет использовать Face ID»)
      if (biometry.requestAccess.isAvailable()) {
        const granted = await biometry.requestAccess({ reason });
        if (!granted) return 'denied';
      }
      // 4. Сам authenticate
      if (!biometry.authenticate.isAvailable()) return 'unavailable';
      const result = await biometry.authenticate({ reason });
      return result.status === 'authorized' ? 'ok' : 'denied';
    } catch {
      return 'unavailable';
    }
  },
};

/**
 * Promise-обёртка над `tg.showPopup` с кнопками — для destructive-подтверждений.
 * resolve(true) если юзер нажал button с id='ok', иначе false. Если попап не поддерживается —
 * fallback на `window.confirm`, чтобы UX в dev-браузере не ломался.
 */
export const confirmAction = (
  message: string,
  title?: string,
  options?: { okText?: string; destructive?: boolean },
): Promise<boolean> => {
  const tg = getWebApp();
  if (!tg?.showPopup) {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      return Promise.resolve(window.confirm(message));
    }
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    try {
      tg.showPopup(
        {
          title,
          message,
          buttons: [
            {
              id: 'ok',
              // destructive — красная кнопка. Уместна для оплаты и отмены, но не для
              // подарка: «активировать бесплатную подписку» не должно выглядеть угрозой.
              type: options?.destructive === false ? 'default' : 'destructive',
              text: options?.okText ?? i18n.t('Confirm'),
            },
            { id: 'cancel', type: 'cancel' },
          ],
        },
        (button_id) => resolve(button_id === 'ok'),
      );
    } catch {
      resolve(false);
    }
  });
};
